import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import toast from "react-hot-toast";
import "./adminusers.css";
import PhotoLightbox from "../../components/PhotoLightbox/PhotoLightbox";

const PAGE_SIZE = 50;
const DEFAULT_AVATAR = "/assets/person/default-avatar.png";

function formatRel(date) {
  if (!date) return "—";
  const ms = Date.now() - new Date(date).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// User.subscriptionState.state → badge (from Stripe webhooks).
const SUB_BADGES = {
  trialing:       { label: "Trial",           cls: "sub-trial",    tip: "On the $1 trial — will be charged when it ends" },
  trial_canceled: { label: "Trial cancelled", cls: "sub-canceled", tip: "Cancelled during the trial — won't be charged" },
  active:         { label: "Paying",          cls: "sub-active",   tip: "Subscription active and paid" },
  canceling:      { label: "Cancelling",      cls: "sub-canceling", tip: "Paid, but set to end at the period end" },
  past_due:       { label: "Past due",        cls: "sub-pastdue",  tip: "Last charge failed — Stripe is retrying" },
  canceled:       { label: "Cancelled",       cls: "sub-canceled", tip: "Subscription ended" },
};
// ?sub= filters (routes/adminAnalytics.js SUB_FILTERS).
const SUB_FILTER_CHIPS = [
  { id: "subscribed", label: "Subscribed" },
  { id: "trialing", label: "On trial" },
  { id: "paying", label: "Paying" },
  { id: "canceled", label: "Cancelled" },
  { id: "past_due", label: "Past due" },
];

function SubBadge({ state }) {
  const b = SUB_BADGES[state?.state];
  if (!b) return null;
  const when = state.state === "trialing" || state.state === "trial_canceled" ? state.trialEnd : state.periodEnd;
  const tip = [b.tip, when && `ends ${new Date(when).toLocaleDateString()}`, state.cancelReason && `reason: ${state.cancelReason.replace(/_/g, " ")}`]
    .filter(Boolean).join(" · ");
  return <span className={`sub-chip ${b.cls}`} title={tip}>{b.label}</span>;
}

function isActive(lastActiveAt) {
  if (!lastActiveAt) return false;
  return Date.now() - new Date(lastActiveAt).getTime() < 5 * 60 * 1000;
}

// ISO alpha-2 country code → flag emoji (regional indicator letters).
function countryFlag(cc) {
  if (!cc || cc.length !== 2) return "🌐";
  return String.fromCodePoint(
    ...[...cc.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

function formatLocation(geo) {
  if (!geo || !geo.country) return null;
  const parts = [geo.city, geo.country].filter(Boolean);
  return parts.join(", ");
}

function AvatarImg({ src, alt, active }) {
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);
  const img = (!src || errored) ? (
    <div className="adminusers-avatar adminusers-avatar--blank" aria-label={alt} />
  ) : (
    <img
      src={src}
      alt={alt || ""}
      className="adminusers-avatar plb-zoomable"
      // Tap the photo for a closer look instead of opening the user's page.
      onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      onError={() => setErrored(true)}
    />
  );
  return (
    <div className="adminusers-avatar-wrap">
      {img}
      <span
        className={`adminusers-status ${active ? "online" : "offline"}`}
        title={active ? "Active now" : "Offline"}
      />
      {open && (
        // Portal clicks still bubble through React to the row — stop them so
        // closing the photo doesn't also open the user's page.
        <span onClick={(e) => e.stopPropagation()}>
          <PhotoLightbox src={src} name={alt} onClose={() => setOpen(false)} />
        </span>
      )}
    </div>
  );
}

// Old standalone page — the list now lives in the dashboard's Users tab, so
// admins keep the tab bar. Redirect any bookmarks/links there.
export default function AdminUsers() {
  return <Navigate to="/admin/dashboard?tab=users" replace />;
}

// Searchable, paginated list of every user. Rendered inside the admin
// dashboard's Users tab.
export function AdminUsersList() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [paidOnly, setPaidOnly] = useState(false);
  const [userType, setUserType] = useState(""); // "" | "clipper" | "streamer"
  const [sub, setSub] = useState("");           // "" | SUB_FILTER_CHIPS id
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [counts, setCounts] = useState({});
  const debounceRef = useRef(null);

  const load = useCallback(async (resetOffset = true) => {
    setLoading(true);
    try {
      const useOffset = resetOffset ? 0 : offset;
      const params = { limit: PAGE_SIZE, offset: useOffset };
      if (search.trim()) params.search = search.trim();
      if (paidOnly) params.paid = 1;
      if (userType) params.userType = userType;
      if (sub) params.sub = sub;
      if (onlineOnly) params.online = 1;
      const res = await axiosInstance.get("/admin/analytics/users", { params });
      setTotal(res.data.total);
      setHasMore(res.data.hasMore);
      setCounts(res.data.counts || {});
      if (resetOffset) {
        setUsers(res.data.users);
        setOffset(res.data.users.length);
      } else {
        setUsers((prev) => [...prev, ...res.data.users]);
        setOffset(useOffset + res.data.users.length);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin only");
        navigate("/");
        return;
      }
      toast.error(err.response?.data?.error || "Failed to load users");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, paidOnly, userType, sub, onlineOnly, offset, navigate]);

  // Reload on filter change (debounced)
  useEffect(() => {
    if (!user) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(true), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, search, paidOnly, userType, sub, onlineOnly]);

  return (
    <div className="adminusers-embedded">
          <h2 className="admindash-section-title">All users</h2>
          <div className="adminusers-tabs">
            <button
              className={`adminusers-tab ${userType === "" ? "on" : ""}`}
              onClick={() => setUserType("")}
            >
              All
            </button>
            <button
              className={`adminusers-tab ${userType === "clipper" ? "on" : ""}`}
              onClick={() => setUserType("clipper")}
            >
              Clippers
            </button>
            <button
              className={`adminusers-tab ${userType === "streamer" ? "on" : ""}`}
              onClick={() => setUserType("streamer")}
            >
              Streamers
            </button>
          </div>

          <div className="adminusers-subfilters">
            <button
              className={`adminusers-subfilter online ${onlineOnly ? "on" : ""}`}
              onClick={() => setOnlineOnly((v) => !v)}
              title="Active in the last 5 minutes"
            >
              <span className="adminusers-online-dot" /> Online now <b>{counts.online ?? 0}</b>
            </button>
            {SUB_FILTER_CHIPS.map((c) => (
              <button
                key={c.id}
                className={`adminusers-subfilter ${sub === c.id ? "on" : ""}`}
                onClick={() => setSub((v) => (v === c.id ? "" : c.id))}
              >
                {c.label} <b>{counts[c.id] ?? 0}</b>
              </button>
            ))}
          </div>

          <div className="adminusers-filters">
            <input
              type="text"
              placeholder="Search by username or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="adminusers-toggle">
              <input
                type="checkbox"
                checked={paidOnly}
                onChange={(e) => setPaidOnly(e.target.checked)}
              />
              Paid users only
            </label>
            <span className="adminusers-count">
              {total.toLocaleString()} {onlineOnly ? "online " : ""}{paidOnly ? "paid " : ""}{userType || "users"}{userType ? "s" : ""}
            </span>
          </div>

          {users.length === 0 && !loading ? (
            <div className="adminusers-empty">No users match these filters.</div>
          ) : (
            <table className="adminusers-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th>Plan</th>
                  <th>Location</th>
                  <th>Phone</th>
                  <th>Joined</th>
                  <th>Last sign-in</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u._id}
                    className="adminusers-row-clickable"
                    onClick={() => navigate(`/admin/users/${u._id}`)}
                  >
                    <td>
                      <div className="adminusers-user">
                        <AvatarImg src={u.profilePicture} alt={u.username} active={isActive(u.lastActiveAt)} />
                        <div className="adminusers-user-meta">
                          <div className="adminusers-user-name">
                            {u.username || "—"}
                            {u.isAdmin && <span className="admin-chip">admin</span>}
                            {!u.verified && <span className="unverified-chip">unverified</span>}
                          </div>
                          <div className="adminusers-user-email">{u.email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {u.onboarding?.userType ? (
                        <span className="type-chip-wrap">
                          <span className="type-chip">{u.onboarding.userType}</span>
                          {u.onboarding.userType === "clipper" && u.onboarding.wantsOpportunities && (
                            <span className="paid-clipper-chip" title="Wants paid clipping opportunities">$</span>
                          )}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {/* Admins don't subscribe: the admin chip is all they get. */}
                      {!u.isAdmin && (
                        <span className="type-chip-wrap">
                          {u.plan && u.plan !== "none" ? (
                            <span className="plan-chip">{u.plan}</span>
                          ) : (
                            !u.subscriptionState?.state && <span className="muted">free</span>
                          )}
                          <SubBadge state={u.subscriptionState} />
                        </span>
                      )}
                    </td>
                    <td>
                      {u.geo?.country ? (
                        <span
                          className="adminusers-loc"
                          title={[u.geo.city, u.geo.region, u.geo.country].filter(Boolean).join(", ")}
                        >
                          {countryFlag(u.geo.country)} {formatLocation(u.geo)}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {u.phoneNumber ? (
                        <span title={u.smsOptedOut ? "Opted out of texts" : undefined}>
                          {u.phoneNumber}{u.smsOptedOut ? " · opted out" : ""}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>{formatRel(u.createdAt)}</td>
                    <td>{formatRel(u.lastSignInAt)}</td>
                    <td>
                      <a
                        href={`/admin/users/${u._id}`}
                        className="adminusers-link"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/admin/users/${u._id}`); }}
                      >
                        Details →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="adminusers-pagination">
            {hasMore && (
              <button
                className="adminusers-more"
                onClick={() => load(false)}
                disabled={loading}
              >
                {loading ? "Loading…" : `Load 50 more (${total - users.length} remaining)`}
              </button>
            )}
            {!hasMore && users.length > 0 && (
              <div className="adminusers-end">End of list</div>
            )}
          </div>
    </div>
  );
}
